<script setup>
import { Popper, PopperContent } from "floating-vue";
import { ref, computed } from "vue";

const props = defineProps({
  shown: {
    type: Boolean,
    default: false,
  },
  placement: {
    type: String,
    default: "top",
  },
  theme: {
    type: String,
    default: "default-tooltip",
  },
  distance: {
    type: Number,
    default: 16,
  },
  triggers: {
    type: Array,
    default: () => ["hover"],
  },
  delay: {
    type: Object,
    default: () => ({ show: 0, hide: 0 }),
  },
  forceOpen: {
    type: Boolean,
    default: false,
  },
  popperTriggers: {
    type: Array,
    default: () => ["hover"],
  },
});

const emit = defineEmits(["update:shown"]);

const VPopper = Popper();
const popperContentRef = ref(null);
const triggerRef = ref(null);

const isShown = computed({
  get: () => props.forceOpen || props.shown,
  set: (value) => {
    if (!props.forceOpen) {
      emit("update:shown", value);
    }
  },
});

const getTargetNodes = () => (triggerRef.value ? [triggerRef.value] : []);
const getReferenceNode = () => triggerRef.value || null;
const getPopperNode = () => popperContentRef.value?.$el || null;

const hidePopper = () => {
  isShown.value = false;
};
</script>

<template>
  <div ref="triggerRef">
    <slot name="trigger" />
  </div>

  <component
    :is="VPopper"
    v-if="triggerRef"
    v-slot="slotProps"
    :theme="theme"
    :shown="isShown"
    :triggers="triggers"
    :placement="placement"
    :distance="distance"
    :delay="delay"
    :popper-triggers="popperTriggers"
    :target-nodes="getTargetNodes"
    :reference-node="getReferenceNode"
    :popper-node="getPopperNode"
    @update:shown="isShown = $event"
  >
    <PopperContent
      ref="popperContentRef"
      :popper-id="slotProps.popperId"
      :theme="theme"
      :shown="slotProps.isShown"
      :mounted="slotProps.shouldMountContent"
      :skip-transition="slotProps.skipTransition"
      :auto-hide="slotProps.autoHide"
      :handle-resize="slotProps.handleResize"
      :classes="slotProps.classes"
      :result="slotProps.result"
      @hide="hidePopper"
      @resize="slotProps.onResize"
    >
      <slot name="content" />
    </PopperContent>
  </component>
</template>
